import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useTopbar } from '../../contexts/TopbarContext';
import ThemeSwitcher from '../ThemeSwitcher/ThemeSwitcher';
import { UserIcon } from '../ui/Icons';
import { NotificationCenter } from '../NotificationCenter';
import { useUnreadCount } from '../../hooks/useNotifications';
import BookmarksTopbar, { BookmarksDisplayMode } from './BookmarksTopbar';
import CombinedSearch from './CombinedSearch';
import { useUserSettings } from '../../modules/admin/settings/useData';
import { useEffectiveSidebarLayout } from '../../hooks/useEffectiveSidebarLayout';
import { cn } from '../../lib/utils';
import { Z_INDEX, zStyle } from '../../lib/zLayers';

type CreateMenuIcon =
  | 'case'
  | 'requisition'
  | 'rfq'
  | 'order'
  | 'journal'
  | 'supplier'
  | 'vessel'
  | 'crew';

interface CreateMenuItem {
  label: string;
  href: string;
  icon: CreateMenuIcon;
  iconBg: string;
  iconColor: string;
  permissionModuleKey?: string;
}

interface CreateMenuGroup {
  label: string;
  items: CreateMenuItem[];
}

function renderCreateMenuIcon(icon: CreateMenuIcon): React.ReactNode {
  switch (icon) {
    case 'case':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      );
    case 'requisition':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    case 'rfq':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      );
    case 'order':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      );
    case 'journal':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      );
    case 'supplier':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      );
    case 'vessel':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3l7 4v6c0 4-3 7-7 8-4-1-7-4-7-8V7l7-4z" />
        </svg>
      );
    case 'crew':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    default:
      return null;
  }
}

interface TopbarProps {
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  onToggleMobileMenu?: () => void;
  isMobileMenuOpen?: boolean;
  /** When true (used by mobile layout), hide bookmarks/topbar extras */
  hideBookmarks?: boolean;
  hideThemeSwitcher?: boolean;
  hideSearch?: boolean;
  hideNotifications?: boolean;
   /** When true (used by mobile layout), keep only Sign out in user menu and hide extra action buttons */
   simpleUserMenu?: boolean;
}

const Topbar: React.FC<TopbarProps> = ({
  title,
  subtitle: staticSubtitle,
  icon,
  onToggleMobileMenu,
  isMobileMenuOpen = false,
  hideBookmarks = false,
  hideThemeSwitcher = false,
  hideSearch = false,
  hideNotifications = false,
   simpleUserMenu = false,
}) => {
  const { subtitle: dynamicSubtitle, titleOverride, rightActions } = useTopbar();
  const subtitle = dynamicSubtitle || staticSubtitle;
  const displayTitle = titleOverride ?? title;
  const { logout, user, can, permissionsLoaded } = useAuth();
  const { colors } = useTheme();
  const { appConfig } = useSettings();
  const navigate = useNavigate();
  const isCompact = false;
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const userButtonRef = useRef<HTMLButtonElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notificationsAnchorRef = useRef<HTMLDivElement>(null);
  const [userMenuPos, setUserMenuPos] = useState<{ top: number; right: number } | null>(null);
  const { count: unreadCount } = useUnreadCount();
  const { userSettings } = useUserSettings();
  
  const displayName =
    user?.userDisplayName?.trim() ||
    [user?.userFirstName, user?.userLastName].filter(Boolean).join(' ') ||
    user?.userEmail ||
    '';
  const userInitial = displayName ? displayName.charAt(0).toUpperCase() : 'U';

  // Determine bookmarks display mode - default to 'sidebar' (not shown in topbar)
  // On mobile/tablet, auto-switch from pins to dropdown for better UX
  const userPreferredMode = userSettings?.bookmarksDisplayMode;
  const sidebarLayout = useEffectiveSidebarLayout();
  const isTopMenuLayout = sidebarLayout === 'topmenu' || sidebarLayout === 'sidebar_topmenu';
  const bookmarksDisplayMode: BookmarksDisplayMode = hideBookmarks
    ? 'none'
    : isTopMenuLayout
      ? 'none'
      : (userPreferredMode === 'sidebar' || userPreferredMode === 'none')
        ? 'none'
        : isCompact && userPreferredMode === 'pins'
          ? 'dropdown' // Auto-switch pins to dropdown on mobile/tablet
          : (userPreferredMode || 'none');

  // Update document title with app name
  useEffect(() => {
    if (appConfig.system.appName) {
      const pageTitle = displayTitle ?? title;
      document.title = pageTitle
        ? `${pageTitle} - ${appConfig.system.appName}` 
        : appConfig.system.appName;
    }
  }, [displayTitle, title, appConfig.system.appName]);

  const handleLogout = async () => {
    await logout();
  };

  const updateUserMenuPosition = () => {
    const el = userButtonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setUserMenuPos({
      top: rect.bottom + 8,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  };

  useLayoutEffect(() => {
    if (!showUserMenu) {
      setUserMenuPos(null);
      return;
    }
    updateUserMenuPosition();
  }, [showUserMenu]);

  useEffect(() => {
    if (!showUserMenu) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (userButtonRef.current?.contains(t) || userMenuRef.current?.contains(t)) return;
      setShowUserMenu(false);
    };
    const onReposition = () => updateUserMenuPosition();
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [showUserMenu]);

  useEffect(() => {
    if (!isTopMenuLayout && showUserMenu) setShowUserMenu(false);
  }, [isTopMenuLayout, showUserMenu]);

  const addMenuGroups: CreateMenuGroup[] = [
    {
      label: 'Finance',
      items: [
        { label: 'Journal entry', href: '/accounting/journals/new', icon: 'journal', iconBg: 'bg-violet-100 dark:bg-violet-500/20', iconColor: 'text-violet-600 dark:text-violet-400', permissionModuleKey: 'acc_documents' },
      ],
    },
    {
      label: 'Supplies',
      items: [
        { label: 'Supply case', href: '/supplies/supply-cases/new', icon: 'case', iconBg: 'bg-indigo-100 dark:bg-indigo-500/20', iconColor: 'text-indigo-600 dark:text-indigo-400', permissionModuleKey: 'supply_cases' },
        { label: 'Requisition', href: '/supplies/requisitions/new', icon: 'requisition', iconBg: 'bg-sky-100 dark:bg-sky-500/20', iconColor: 'text-sky-600 dark:text-sky-400', permissionModuleKey: 'supply_requisitions' },
        { label: 'RFQ', href: '/supplies/supplier-enquiries/new', icon: 'rfq', iconBg: 'bg-teal-100 dark:bg-teal-500/20', iconColor: 'text-teal-600 dark:text-teal-400', permissionModuleKey: 'supplier_enquiries' },
        { label: 'Purchase order', href: '/supplies/supply-purchase-orders/new', icon: 'order', iconBg: 'bg-amber-100 dark:bg-amber-500/20', iconColor: 'text-amber-600 dark:text-amber-400', permissionModuleKey: 'supply_purchase_orders' },
      ],
    },
    {
      label: 'Reference',
      items: [
        { label: 'Entity', href: '/entities/new', icon: 'supplier', iconBg: 'bg-emerald-100 dark:bg-emerald-500/20', iconColor: 'text-emerald-600 dark:text-emerald-400', permissionModuleKey: 'suppliers' },
        { label: 'Vessel', href: '/parameters/vessels/new', icon: 'vessel', iconBg: 'bg-blue-100 dark:bg-blue-500/20', iconColor: 'text-blue-600 dark:text-blue-400', permissionModuleKey: 'vessels' },
        { label: 'Crew file', href: '/crew/new', icon: 'crew', iconBg: 'bg-rose-100 dark:bg-rose-500/20', iconColor: 'text-rose-600 dark:text-rose-400', permissionModuleKey: 'crew' },
      ],
    },
  ];

  const canShowCreateItem = (permissionModuleKey?: string) =>
    !permissionModuleKey ||
    user?.role === 'ADMIN' ||
    (permissionsLoaded && can(permissionModuleKey, 'view'));

  const filteredAddMenuGroups = addMenuGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => canShowCreateItem(item.permissionModuleKey)),
    }))
    .filter((group) => group.items.length > 0);

  const showCreateMenu = !simpleUserMenu && filteredAddMenuGroups.length > 0;

  return (
    <header className={`${colors.topbarBackground} shadow-sm border-b-2 ${colors.topbarBorder} sticky top-0 z-30`}>
      <div className={`flex items-center justify-between ${isCompact ? 'px-3 py-3' : 'px-6 py-4'}`}>
        {/* Left section: Menu toggle, icon, title */}
        <div className={`flex items-center ${isCompact ? 'space-x-2' : 'space-x-3'} min-w-0 flex-shrink-0`}>
          {onToggleMobileMenu && (
            <button
              onClick={onToggleMobileMenu}
              className={`lg:hidden p-2 rounded-md ${colors.textMuted} hover:${colors.sidebarTextHover} hover:${colors.sidebarActive} focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white`}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          )}
          {icon && (
            <div className={`${colors.textSecondary} ${isCompact ? 'flex-shrink-0' : ''}`}>
              {icon}
            </div>
          )}
          {displayTitle ? (
            <div className="min-w-0">
              <h1 className={`${isCompact ? 'text-lg' : 'text-2xl'} font-bold ${colors.text} truncate`}>
                {displayTitle}
              </h1>
              {subtitle && !isCompact && (
                <p className={`text-sm ${colors.textSecondary} mt-0.5 truncate`}>
                  {subtitle}
                </p>
              )}
            </div>
          ) : null}
        </div>
        
        {/* Center section: Combined search (pages + records) + optional bookmark pins */}
        <div className="flex-1 flex justify-center items-center gap-4 min-w-0 px-4">
          <CombinedSearch />
          {bookmarksDisplayMode === 'pins' && (
            <BookmarksTopbar displayMode="pins" maxPins={7} />
          )}
        </div>
        
        {/* Right section: Add dropdown, Search, dropdown bookmarks, notifications, user menu */}
        <div className={`flex items-center ${isCompact ? 'space-x-2' : 'space-x-4'} flex-shrink-0`}>
          {rightActions ? <div className="flex items-center min-w-0">{rightActions}</div> : null}
          {/* Add dropdown (hidden when user has no create targets or in simpleUserMenu mode) */}
          {showCreateMenu && (
            <div className="relative">
              <button
                onClick={() => setShowAddMenu(!showAddMenu)}
                className={cn(
                  'relative flex items-center justify-center rounded-lg transition-colors',
                  isCompact ? 'p-2 min-w-[44px] min-h-[44px]' : 'p-2',
                  colors.textMuted,
                  `hover:${colors.textSecondary}`,
                  'hover:bg-black/5 dark:hover:bg-white/5',
                  'focus:outline-none focus:ring-2 focus:ring-indigo-500'
                )}
                title="Quick add"
              >
                <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <svg className={cn('w-4 h-4 ml-0.5 shrink-0 transition-transform', showAddMenu && 'rotate-180')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showAddMenu && (
                <>
                  <div className="fixed inset-0 z-10" aria-hidden onClick={() => setShowAddMenu(false)} />
                  <div
                    className={cn(
                      'absolute left-0 mt-2.5 w-72 z-20 overflow-hidden',
                      'rounded-2xl shadow-2xl border backdrop-blur-xl',
                      'animate-in fade-in slide-in-from-top-2 duration-200',
                      colors.surface,
                      colors.border
                    )}
                  >
                    <div className={cn('px-4 pt-4 pb-3', colors.border, 'border-b')}>
                      <div className="flex items-center gap-2">
                        <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-600 dark:text-indigo-400')}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </span>
                        <div>
                          <p className={cn('text-sm font-bold', colors.text)}>Create new</p>
                          <p className={cn('text-xs', colors.textMuted)}>Supply, accounting, or reference record</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-2">
                      {filteredAddMenuGroups.map((group) => (
                        <div key={group.label} className="mb-3 last:mb-0">
                          <p className={cn('px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest', colors.textMuted)}>
                            {group.label}
                          </p>
                          <div className="space-y-0.5">
                            {group.items.map((item) => (
                              <button
                                key={item.label}
                                type="button"
                                onClick={() => {
                                  setShowAddMenu(false);
                                  navigate(item.href);
                                }}
                                className={cn(
                                  'flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium transition-colors',
                                  'border-l-2 border-transparent hover:border-indigo-500 hover:bg-indigo-500/5 dark:hover:bg-indigo-500/10',
                                  colors.text,
                                  colors.interactiveHover
                                )}
                              >
                                <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', item.iconBg, item.iconColor)}>
                                  {renderCreateMenuIcon(item.icon)}
                                </span>
                                <span>{item.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Theme Switcher - only show if enabled and not hidden (e.g. mobile layout) */}
          {!hideThemeSwitcher && appConfig.features.enableDarkMode && <ThemeSwitcher />}
          
          {/* Bookmarks dropdown - only show dropdown mode in this area */}
          {bookmarksDisplayMode === 'dropdown' && (
            <BookmarksTopbar displayMode="dropdown" />
          )}
          
          {/* Notifications */}
          {!hideNotifications && (
            <div className="relative" ref={notificationsAnchorRef}>
              <button
                type="button"
                onClick={() => setShowNotifications(!showNotifications)}
                className={`relative ${isCompact ? 'p-2 min-w-[44px] min-h-[44px]' : 'p-2'} ${colors.textMuted} hover:${colors.textSecondary} focus:outline-none focus:ring-2 focus:ring-teal-500 rounded-lg flex items-center justify-center`}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 block h-5 w-5 rounded-full bg-red-500 text-white text-xs font-semibold flex items-center justify-center ring-2 ring-white">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              {showNotifications && (
                <NotificationCenter
                  anchorRef={notificationsAnchorRef}
                  onClose={() => setShowNotifications(false)}
                />
              )}
            </div>
          )}

          {/* User Menu — only for Top Menu / Sidebar + Top Menu layouts */}
          {isTopMenuLayout && (
          <div className="relative">
            <button
              ref={userButtonRef}
              type="button"
              onClick={() => setShowUserMenu(!showUserMenu)}
              className={`flex items-center ${isCompact ? 'space-x-1' : 'space-x-2'} ${isCompact ? 'p-1.5 min-w-[36px] min-h-[36px]' : 'p-1.5'} rounded-lg bg-transparent hover:bg-black/5 dark:hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
              aria-haspopup="menu"
              aria-expanded={showUserMenu}
            >
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-500/90 to-purple-600/90 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-semibold text-xs">
                  {userInitial}
                </span>
              </div>
              <svg className={`h-3.5 w-3.5 ${colors.textMuted} flex-shrink-0`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showUserMenu &&
              userMenuPos &&
              createPortal(
                <div
                  ref={userMenuRef}
                  role="menu"
                  className={cn(
                    'fixed w-56 rounded-xl shadow-lg py-1 overflow-hidden border',
                    colors.surface,
                    colors.border,
                  )}
                  style={{
                    top: userMenuPos.top,
                    right: userMenuPos.right,
                    ...zStyle(Z_INDEX.accountPopover),
                  }}
                >
                  <div className={cn('px-3.5 py-2.5 border-b', colors.border)}>
                    <p className={cn('text-sm font-medium truncate', colors.text)}>
                      {displayName || 'User'}
                    </p>
                    {user?.userEmail && (
                      <p className={cn('text-xs truncate', colors.textMuted)}>{user.userEmail}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/account-settings?section=profile');
                    }}
                    className={cn(
                      'flex items-center gap-2.5 w-full text-left px-3.5 py-2.5 text-sm',
                      colors.textSecondary,
                      colors.interactiveHover,
                    )}
                  >
                    <UserIcon className="w-4 h-4" />
                    Profile
                  </button>
                  <hr className={cn('my-1', colors.border)} />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setShowUserMenu(false);
                      void handleLogout();
                    }}
                    className={cn(
                      'flex items-center gap-2.5 w-full text-left px-3.5 py-2.5 text-sm text-red-600 dark:text-red-400',
                      colors.interactiveHover,
                    )}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Logout
                  </button>
                </div>,
                document.body,
              )}
          </div>
          )}
        </div>
      </div>
      
      {/* Bookmarks bar - shown below main topbar content */}
      {bookmarksDisplayMode === 'bar' && (
        <BookmarksTopbar displayMode="bar" />
      )}
    </header>
  );
};

export default Topbar;

